import { createUrlQueryParams } from "../utils/urlUtils";
import axiosInstance from "./axios";
import { Family } from "./models/fullProductData.models";
import { SearchProduct } from "./models/searchProduct.models";

const apiUrl = import.meta.env.VITE_BACKEND_API_URL;

export const fetchSearchProductData = async (
  bearerToken: string,
  companyId: number,
  isEanSearch: boolean = false,
  page?: number,
  searchStrings?: string[],
  limit?: number,
  exactMatch?: boolean,
  sortBy?: string,
  asc?: boolean,
): Promise<{ data: SearchProduct[]; isSynkkaData: boolean; _count?: number }> => {
  try {
    let url = createUrlQueryParams(
      `${apiUrl}/Products/${companyId}/searchProducts`,
      searchStrings,
      page,
      limit,
      exactMatch
    );

    if (sortBy) {
      const backendSortBy = sortBy === "type" ? "family" : sortBy;
      url += url.includes("?")
        ? `&sortBy=${encodeURIComponent(backendSortBy)}`
        : `?sortBy=${encodeURIComponent(backendSortBy)}`;
    }

    if (asc !== undefined) {
      url += url.includes("?") ? `&asc=${asc}` : `?asc=${asc}`;
    }

    const response = await axiosInstance.get(url, {
      headers: {
        Authorization: `Bearer ${bearerToken}`,
      },
    });

    const totalHeader =
      response.headers["total"] || response.headers["x-total-count"];
    const total = totalHeader ? parseInt(totalHeader, 10) : undefined;

    // If response data is empty array, fetchFullProductData without companyId
    // This is a fallback to ensure we get product data even if the company-specific endpoint returns no results
    if (response.data?.length === 0 && isEanSearch) {
      const secondResult = await fetchSynkkaSearchProductData(
        bearerToken,
        searchStrings,
        page,
        limit
      );

      if (secondResult?.data?.length > 0) {
        return {
          data: secondResult.data,
          isSynkkaData: true,
          _count: secondResult._count,
        };
      }
    }

    return {
      data: response.data,
      isSynkkaData: false,
      _count: total,
    };
  } catch (error) {
    console.error("Failed to fetch search product data:", error);
    throw error;
  }
};

export const fetchSynkkaSearchProductData = async (
  bearerToken: string,
  searchStrings?: string[],
  page?: number,
  limit?: number
): Promise<{ data: SearchProduct[]; _count?: number }> => {
  try {
    // Synkka EANs are always 14 characters long, so we need to pad them with leading zeros if they are shorter
    const modifiedSearchStrings = searchStrings?.map((str) => str.padStart(14, "0"));

    const url = createUrlQueryParams(
      `${apiUrl}/Products/searchProducts`,
      modifiedSearchStrings,
      page,
      limit,
      true
    );

    const response = await axiosInstance.get(url, {
      headers: {
        Authorization: `Bearer ${bearerToken}`,
      },
    });

    const totalHeader =
      response.headers["total"] || response.headers["x-total-count"];
    const total = totalHeader ? parseInt(totalHeader, 10) : undefined;

    return {
      data: response.data,
      _count: total,
    };
  } catch (error) {
    console.error("Failed to fetch synkka search product data:", error);
    throw error;
  }
};
